import { useEffect, useRef, useState } from "react";
import { DADES, TAULES, ddl } from "./model";

// ─── SQLite al navegador (sql.js) ─────────────────────────────────────────────

type SqlDb = { exec: (sql: string) => { columns: string[]; values: unknown[][] }[]; run: (sql: string, params?: unknown[]) => void; export: () => Uint8Array };
type SqlJs = { Database: new () => SqlDb };

const SQLJS = "https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.10.3/";

let sqlPromise: Promise<SqlJs> | null = null;
function loadSql(): Promise<SqlJs> {
  sqlPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `${SQLJS}sql-wasm.js`;
    s.onload = () => (window as unknown as { initSqlJs: (o: object) => Promise<SqlJs> }).initSqlJs({ locateFile: (f: string) => SQLJS + f }).then(resolve, reject);
    s.onerror = () => reject(new Error("No s'ha pogut carregar sql.js"));
    document.head.appendChild(s);
  });
  return sqlPromise;
}

function buildDb(SQL: SqlJs): SqlDb {
  const db = new SQL.Database();
  db.exec("PRAGMA foreign_keys = ON;");
  db.exec(ddl("sqlite"));
  db.exec("BEGIN");
  for (const t of TAULES) {
    const cols = t.cols.map((c) => c.nom);
    const sql = `INSERT INTO ${t.nom} (${cols.join(", ")}) VALUES (${cols.map(() => "?").join(", ")})`;
    for (const row of DADES[t.nom as keyof typeof DADES] as unknown as Record<string, unknown>[]) db.run(sql, cols.map((c) => row[c] ?? null));
  }
  db.exec("COMMIT");
  return db;
}

/**
 * L'script sencer per a MySQL 8 / MariaDB (Local): CREATE TABLE i tots els INSERT. Comença esborrant
 * les taules (al revés, per les FK), així es pot tornar a importar tantes vegades com calgui.
 */
export function script(): string {
  const q = (v: unknown) => (v == null ? "NULL" : typeof v === "number" ? String(v) : `'${String(v).replace(/\\/g, "\\\\").replace(/'/g, "''")}'`);
  const drops = [...TAULES].reverse().map((t) => `DROP TABLE IF EXISTS ${t.nom};`);
  const parts = [
    "-- cotx.es · model relacional per a MySQL 8 / MariaDB",
    "-- Generat a https://www.cotx.es/dades",
    "-- A Local (localwp.com): Database → Open Adminer → Import → aquest fitxer → Execute.",
    "-- Les taules conviuen amb les de WordPress (wp_*) a la mateixa base de dades.",
    "",
    "SET NAMES utf8mb4;",
    ...drops,
    "",
    ddl("mysql"),
    "",
  ];
  for (const t of TAULES) {
    const cols = t.cols.map((c) => c.nom);
    const rows = DADES[t.nom as keyof typeof DADES] as unknown as Record<string, unknown>[];
    parts.push(`-- ${t.nom}: ${rows.length} files`);
    for (let i = 0; i < rows.length; i += 100) {
      const chunk = rows.slice(i, i + 100).map((r) => `  (${cols.map((c) => q(r[c])).join(", ")})`);
      parts.push(`INSERT INTO ${t.nom} (${cols.join(", ")}) VALUES\n${chunk.join(",\n")};`);
    }
    parts.push("");
  }
  return parts.join("\n");
}

function csv(columns: string[], values: unknown[][]): string {
  const q = (v: unknown) => (v == null ? "" : /[",\n;]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  return [columns, ...values].map((r) => r.map(q).join(",")).join("\n");
}

export function download(name: string, data: BlobPart, type: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// ─── SQL amb colors ───────────────────────────────────────────────────────────

const KW = /\b(UNION ALL|SELECT|DISTINCT|FROM|WHERE|GROUP BY|HAVING|ORDER BY|LIMIT|DESC|ASC|AS|JOIN|LEFT JOIN|INNER JOIN|ON|AND|OR|NOT|IN|IS|NULL|LIKE|BETWEEN|EXISTS|COUNT|AVG|SUM|MIN|MAX|ROUND|CASE|WHEN|THEN|ELSE|END|CREATE TABLE|PRIMARY KEY|FOREIGN KEY|REFERENCES|UNIQUE|CHECK|AUTO_INCREMENT|INT|SMALLINT|TINYINT|VARCHAR|DECIMAL|DATE|INSERT INTO|VALUES|SUBSTR)\b/g;

export function Sql({ code }: { code: string }) {
  const html = code
    .split("\n")
    .map((line) => {
      const at = line.indexOf("--");
      const [src, comment] = at < 0 ? [line, ""] : [line.slice(0, at), line.slice(at)];
      const e = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
      const body = e(src)
        .replace(/('[^']*')/g, '<span class="tk-s">$1</span>')
        .replace(KW, '<span class="tk-k">$1</span>')
        .replace(/\b(\d+(?:\.\d+)?)\b/g, '<span class="tk-n">$1</span>');
      return comment ? `${body}<span class="tk-c">${e(comment)}</span>` : body;
    })
    .join("\n");
  return <pre className="sql overflow-x-auto rounded-xl border border-border bg-secondary/50 p-3 sm:p-4 font-mono text-[12.5px] leading-relaxed text-foreground" dangerouslySetInnerHTML={{ __html: html }} />;
}

// ─── Exercicis ────────────────────────────────────────────────────────────────

export const EXERCICIS: { titol: string; tema: string; enunciat: string; sql: string; nota: string }[] = [
  {
    titol: "Els més potents",
    tema: "SELECT · JOIN · ORDER BY",
    enunciat: "Llista els 10 models més potents amb el nom de la marca, el combustible i la potència.",
    sql: `SELECT ma.Nombre AS Marca, mo.Nombre AS Modelo, c.Nombre AS Combustible, mo.Potencia
FROM Modelos mo
JOIN Marcas ma ON ma.MarcaID = mo.MarcaID
JOIN Combustibles c ON c.CombustibleID = mo.CombustibleID
ORDER BY mo.Potencia DESC
LIMIT 10;`,
    nota: "Dos JOIN: cada FK de Modelos porta a una taula. Sense JOIN només veuríem els identificadors.",
  },
  {
    titol: "Busca pel nom",
    tema: "WHERE · LIKE",
    enunciat: "Quins models porten «Sport» al nom? Mostra'n la marca i el maleter.",
    sql: `SELECT MarcaID, Nombre, Maletero
FROM Modelos
WHERE Nombre LIKE '%Sport%'
ORDER BY Maletero DESC;`,
    nota: "% vol dir «qualsevol text». '%Sport' només trobaria els que acaben així.",
  },
  {
    titol: "Preu per família",
    tema: "GROUP BY · AVG · COUNT",
    enunciat: "Per a cada grup de combustible (Elèctric, Electrificat, Combustió), quants models hi ha i quin és el preu mitjà al juliol?",
    sql: `SELECT c.Grupo, COUNT(*) AS Modelos, ROUND(AVG(p.Precio)) AS PrecioMedio
FROM Precios p
JOIN Modelos mo ON mo.ModeloID = p.ModeloID
JOIN Combustibles c ON c.CombustibleID = mo.CombustibleID
WHERE p.EdicionID = 2
GROUP BY c.Grupo
ORDER BY PrecioMedio DESC;`,
    nota: "El preu no és a Modelos sinó a Precios (una fila per edició): cal triar l'edició amb el WHERE abans d'agrupar.",
  },
  {
    titol: "Marques grans",
    tema: "GROUP BY · HAVING",
    enunciat: "Quines marques tenen més de 20 models a la llista?",
    sql: `SELECT ma.Nombre, COUNT(*) AS Modelos
FROM Modelos mo
JOIN Marcas ma ON ma.MarcaID = mo.MarcaID
GROUP BY ma.Nombre
HAVING COUNT(*) > 20
ORDER BY Modelos DESC;`,
    nota: "WHERE filtra files abans d'agrupar; HAVING filtra els grups ja fets.",
  },
  {
    titol: "Qui ha apujat el preu",
    tema: "N:M · la mateixa taula dues vegades",
    enunciat: "Quins models han apujat el preu de març (edició 1) a juliol (edició 2), i quant en percentatge? Els 15 que més.",
    sql: `SELECT mo.MarcaID, mo.Nombre,
       m.Precio AS Marzo, j.Precio AS Julio,
       ROUND(100.0 * (j.Precio - m.Precio) / m.Precio, 1) AS Variacion
FROM Precios m
JOIN Precios j ON j.ModeloID = m.ModeloID AND j.EdicionID = 2
JOIN Modelos mo ON mo.ModeloID = m.ModeloID
WHERE m.EdicionID = 1 AND j.Precio > m.Precio
ORDER BY Variacion DESC
LIMIT 15;`,
    nota: "Precios surt dos cops amb dos àlies (m i j): un per a cada edició. És la gràcia de la taula pont.",
  },
  {
    titol: "Matrícules sense marca",
    tema: "LEFT JOIN · IS NULL",
    enunciat: "Quines matriculacions són de marques que no tenim al catàleg? Agrupa-les pel model.",
    sql: `SELECT mt.Modelo, COUNT(*) AS Veces
FROM Matriculaciones mt
LEFT JOIN Marcas ma ON ma.MarcaID = mt.MarcaID
WHERE ma.MarcaID IS NULL
GROUP BY mt.Modelo
ORDER BY Veces DESC;`,
    nota: "Un JOIN normal les faria desaparèixer. El LEFT JOIN les guarda i posa NULL a la part de Marcas.",
  },
  {
    titol: "Més cars que la mitjana",
    tema: "Subconsulta",
    enunciat: "Quants models costen més que el preu mitjà de juliol? I quin és aquest preu mitjà?",
    sql: `SELECT COUNT(*) AS Modelos,
       (SELECT ROUND(AVG(Precio)) FROM Precios WHERE EdicionID = 2) AS Media
FROM Precios
WHERE EdicionID = 2
  AND Precio > (SELECT AVG(Precio) FROM Precios WHERE EdicionID = 2);`,
    nota: "La subconsulta calcula la mitjana un cop i la consulta de fora la fa servir per comparar.",
  },
  {
    titol: "Els que van durar més",
    tema: "Camps calculats",
    enunciat: "Quins models descatalogats es van fabricar durant més de 20 anys?",
    sql: `SELECT MarcaID, Nombre, AnioInicio, AnioFin,
       AnioFin - AnioInicio AS Anios
FROM Descatalogados
WHERE AnioFin - AnioInicio > 20
ORDER BY Anios DESC;`,
    nota: "Si haguéssim guardat «2000–2009» en un sol camp de text, aquesta resta no es podria fer.",
  },
  {
    titol: "Novetats del juliol",
    tema: "NOT EXISTS",
    enunciat: "Quins models tenen preu al juliol però no al març (és a dir, són nous)? Compta'ls per marca.",
    sql: `SELECT mo.MarcaID, COUNT(*) AS Nuevos
FROM Precios j
JOIN Modelos mo ON mo.ModeloID = j.ModeloID
WHERE j.EdicionID = 2
  AND NOT EXISTS (
    SELECT 1 FROM Precios m
    WHERE m.ModeloID = j.ModeloID AND m.EdicionID = 1
  )
GROUP BY mo.MarcaID
ORDER BY Nuevos DESC;`,
    nota: "L'estat «Nou» de la revista no es guarda: es dedueix de les dades. Guardar-lo seria redundant.",
  },
  {
    titol: "Matrícules per mes",
    tema: "Dates",
    enunciat: "Quantes matriculacions hi ha de cada mes?",
    sql: `SELECT SUBSTR(Fecha, 1, 7) AS Mes, COUNT(*) AS Matriculaciones
FROM Matriculaciones
GROUP BY Mes
ORDER BY Mes DESC;`,
    nota: "Com que Fecha és DATE (AAAA-MM-DD), els set primers caràcters són l'any i el mes. A MySQL també es pot fer amb DATE_FORMAT(Fecha, '%Y-%m').",
  },
];

// ─── Consola ──────────────────────────────────────────────────────────────────

export function Consola({ query }: { query: { sql: string; n: number } }) {
  const [db, setDb] = useState<SqlDb | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sql, setSql] = useState(query.sql);
  const [result, setResult] = useState<{ columns: string[]; values: unknown[][] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ms, setMs] = useState<number | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadSql()
      .then((SQL) => setDb(buildDb(SQL)))
      .catch((e) => setLoadError(String(e.message ?? e)));
  }, []);

  function run(text = sql) {
    if (!db) return;
    const t = performance.now();
    try {
      const res = db.exec(text);
      setResult(res[res.length - 1] ?? { columns: [], values: [] });
      setError(null);
    } catch (e) {
      setError(String((e as Error).message ?? e));
      setResult(null);
    }
    setMs(performance.now() - t);
  }

  // Un exercici («Prova-la») hi posa la seva consulta.
  useEffect(() => {
    setSql(query.sql);
    if (db) run(query.sql);
  }, [query.n, db]);

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {TAULES.map((t) => (
          <button key={t.nom} type="button" onClick={() => { const q = `SELECT * FROM ${t.nom} LIMIT 50;`; setSql(q); run(q); }} className="h-8 px-3 rounded-full border border-border font-mono text-xs text-muted-foreground hover:text-foreground">
            {t.nom}
          </button>
        ))}
      </div>
      <textarea
        ref={area}
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            run();
          }
        }}
        spellCheck={false}
        rows={8}
        aria-label="Consulta SQL"
        className="w-full rounded-xl border border-border bg-secondary/50 p-3 font-mono text-[13px] leading-relaxed text-foreground outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button type="button" onClick={() => run()} disabled={!db} className="h-9 px-4 rounded-full bg-foreground text-background text-sm font-medium disabled:opacity-50">
          {db ? "Executa" : loadError ? "No disponible" : "Carregant SQLite…"}
        </button>
        {result && result.columns.length > 0 && (
          <button type="button" onClick={() => download("consulta.csv", csv(result.columns, result.values), "text/csv")} className="h-9 px-4 rounded-full border border-border text-sm text-foreground">
            Resultat en CSV
          </button>
        )}
        {ms != null && result && <span className="text-xs text-muted-foreground tabular-nums">{result.values.length} files · {ms.toFixed(1).replace(".", ",")} ms</span>}
      </div>
      {loadError && <p className="mt-3 text-sm text-red-600">{loadError}</p>}
      {error && <p className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 font-mono text-xs text-red-700 dark:text-red-300">{error}</p>}
      {result && result.columns.length > 0 && (
        <div className="mt-4 max-h-[28rem] overflow-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-secondary">
              <tr>
                {result.columns.map((c) => (
                  <th key={c} className="px-3 py-2 text-left font-mono text-xs font-semibold text-foreground whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.values.slice(0, 500).map((row, i) => (
                <tr key={i} className="border-t border-border">
                  {row.map((v, j) => (
                    <td key={j} className={`px-3 py-1.5 whitespace-nowrap ${typeof v === "number" ? "text-right tabular-nums" : ""} ${v == null ? "text-muted-foreground" : "text-foreground"}`}>
                      {v == null ? "NULL" : String(v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {result.values.length > 500 && <p className="px-3 py-2 text-xs text-muted-foreground">Es mostren les 500 primeres de {result.values.length}.</p>}
        </div>
      )}
      {result && !result.columns.length && !error && <p className="mt-3 text-sm text-muted-foreground">Fet. La consulta no retorna files.</p>}
    </div>
  );
}

export { buildDb, loadSql };
